import { Component, ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/ui/logo'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: unknown) {
    console.error('Error boundary caught an error:', error, errorInfo)
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className='min-h-screen bg-gradient-to-br from-background via-background to-secondary/20'>
          {/* Header with Logo */}
          <div className='absolute top-0 left-0 right-0 p-6'>
            <div className='max-w-6xl mx-auto'>
              <Logo size='md' />
            </div>
          </div>

          <div className='min-h-screen flex items-center justify-center p-4'>
            <Card className='w-full max-w-md shadow-xl'>
              <CardHeader className='text-center'>
                <div className='mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4'>
                  <AlertTriangle className='w-6 h-6 text-destructive' />
                </div>
                <CardTitle>Something went wrong</CardTitle>
                <CardDescription>An error occurred while loading the dashboard</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                {this.state.error && (
                  <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-3'>
                    <p className='text-sm text-destructive font-mono'>{this.state.error.message}</p>
                  </div>
                )}
                <Button onClick={() => window.location.reload()} className='w-full'>
                  <RefreshCw className='w-4 h-4 mr-2' />
                  Reload Page
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
